import type { CSSProperties } from 'react';

type SkeletonProps = {
  className?: string;
  style?: CSSProperties;
  /** Accessible label while content loads */
  label?: string;
};

/** Single shimmer block */
export function Skeleton({ className = '', style, label = 'Loading' }: SkeletonProps) {
  return (
    <span
      className={`skeleton ${className}`.trim()}
      style={style}
      aria-hidden={label ? undefined : true}
      aria-label={label}
      role="presentation"
    />
  );
}

/** Catalog ghost rows matching year / title / format / arrow columns. */
export function ListSkeleton({ rows = 12 }: { rows?: number }) {
  const count = Math.max(1, rows);

  return (
    <>
      {Array.from({ length: count }, (_, i) => (
        <div className="skeleton-list-row" key={i} aria-hidden={i > 0}>
          <Skeleton
            className="skeleton-cell skeleton-year"
            label={i === 0 ? 'Loading' : undefined}
          />
          <Skeleton
            className="skeleton-cell skeleton-title"
            style={{ width: `${48 + ((i * 19) % 40)}%` }}
            label={undefined}
          />
          <Skeleton
            className="skeleton-cell skeleton-format"
            label={undefined}
          />
          <Skeleton
            className="skeleton-cell skeleton-arrow"
            label={undefined}
          />
        </div>
      ))}
    </>
  );
}

/** Infos bio + contact form — same DOM shell as the loaded panel. */
export function InfoSkeleton() {
  return (
    <>
      <div className="skeleton-info-portrait" aria-hidden />
      <div
        className="bio skeleton-info-bio"
        aria-busy="true"
        aria-label="Loading info"
      >
        <Skeleton style={{ width: '100%' }} label={undefined} />
        <Skeleton style={{ width: '97%' }} label={undefined} />
        <Skeleton style={{ width: '94%' }} label={undefined} />
        <Skeleton style={{ width: '88%' }} label={undefined} />
        <Skeleton style={{ width: '72%' }} label={undefined} />
      </div>
      <div className="contact-heading" aria-hidden>
        <Skeleton className="skeleton-contact-heading" label={undefined} />
        <Skeleton className="skeleton-contact-arrow" label={undefined} />
      </div>
      <div className="contact-form skeleton-info-contact" aria-hidden>
        <div className="form-pair">
          <Skeleton className="skeleton-input" label={undefined} />
          <Skeleton className="skeleton-input" label={undefined} />
        </div>
        <div className="form-pair">
          <Skeleton className="skeleton-input" label={undefined} />
          <Skeleton className="skeleton-input" label={undefined} />
        </div>
        <Skeleton className="skeleton-input skeleton-textarea" label={undefined} />
        <div className="contact-actions">
          <Skeleton className="skeleton-input" label={undefined} />
          <Skeleton className="skeleton-input" label={undefined} />
        </div>
      </div>
    </>
  );
}

/** Right-column portrait placeholder until image/video is ready. */
export function PortraitSkeleton() {
  return (
    <div className="skeleton-portrait" aria-busy="true" aria-label="Loading portrait" />
  );
}

/** Square artwork / photo stage placeholder (release + photo detail). */
export function ArtworkSkeleton({ label = 'Loading artwork' }: { label?: string }) {
  return (
    <div className="skeleton-artwork" aria-busy="true" aria-label={label} />
  );
}

/** Ghost track rows matching mobile/desktop track list geometry. */
export function TrackListSkeleton({ rows = 8 }: { rows?: number }) {
  const count = Math.max(1, rows);
  return (
    <div className="skeleton-track-list" aria-busy="true" aria-label="Loading tracks">
      {Array.from({ length: count }, (_, i) => (
        <div className="skeleton-track-row" key={i} aria-hidden={i > 0}>
          <Skeleton
            className="skeleton-cell skeleton-track-no"
            label={i === 0 ? 'Loading tracks' : undefined}
          />
          <Skeleton
            className="skeleton-cell skeleton-track-title"
            style={{ width: `${52 + ((i * 13) % 34)}%` }}
            label={undefined}
          />
          <Skeleton
            className="skeleton-cell skeleton-track-time"
            label={undefined}
          />
        </div>
      ))}
    </div>
  );
}

/** Music Videos shelf shell — same classes as loaded MusicVideoShelf. */
function MusicVideoShelfSkeleton({ cards = 2 }: { cards?: number }) {
  const count = Math.max(1, cards);
  return (
    <section className="music-video-shelf" aria-hidden>
      <h2 className="music-video-shelf-title">Music Videos</h2>
      <div className="music-video-shelf-scroller">
        {Array.from({ length: count }, (_, i) => (
          <div className="music-video-card" key={i}>
            <span className="music-video-card-art skeleton-mv-art" />
            <span className="music-video-card-meta">
              <Skeleton
                className="skeleton-mv-name"
                style={{ width: `${58 + ((i * 17) % 28)}%` }}
                label={undefined}
              />
            </span>
          </div>
        ))}
      </div>
    </section>
  );
}

/** Full release detail chrome for direct `/music/:id` loads before shell data exists. */
export function ReleaseDetailSkeleton({ trackRows = 8 }: { trackRows?: number }) {
  return (
    <div className="detail-view" aria-busy="true" aria-label="Loading release">
      <div className="detail-scroll">
        <div className="detail-align">
          <div className="detail-heading">
            <div className="detail-heading-lead">
              <div className="detail-heading-main">
                <Skeleton
                  className="skeleton-detail-title"
                  style={{ width: '58%', height: '1.15em' }}
                  label="Loading release"
                />
              </div>
              <div
                className="detail-title-actions detail-title-actions--desktop"
                aria-hidden
              >
                <Skeleton className="skeleton-detail-action" label={undefined} />
                <Skeleton className="skeleton-detail-action" label={undefined} />
                <Skeleton className="skeleton-detail-action" label={undefined} />
              </div>
            </div>
            <p className="detail-artist" aria-hidden>
              <Skeleton
                className="skeleton-detail-artist"
                style={{ width: '28%', height: '0.9em' }}
                label={undefined}
              />
            </p>
            <div
              className="detail-heading-transport skeleton-detail-transport"
              aria-hidden
            >
              <Skeleton className="skeleton-detail-circle" label={undefined} />
              <Skeleton className="skeleton-detail-play" label={undefined} />
              <Skeleton className="skeleton-detail-circle" label={undefined} />
              <Skeleton className="skeleton-detail-circle" label={undefined} />
            </div>
          </div>
          <div className="track-list">
            <div className="track-scroll">
              <TrackListSkeleton rows={trackRows} />
              <MusicVideoShelfSkeleton />
              <div className="release-apple-meta" aria-hidden>
                <Skeleton
                  style={{ width: '42%', height: '0.85em' }}
                  label={undefined}
                />
                <Skeleton
                  style={{ width: '56%', height: '0.85em' }}
                  label={undefined}
                />
                <Skeleton
                  style={{ width: '38%', height: '0.85em' }}
                  label={undefined}
                />
              </div>
              <div className="release-about" aria-hidden>
                <Skeleton
                  style={{ width: '100%', height: '0.85em' }}
                  label={undefined}
                />
                <Skeleton
                  style={{ width: '94%', height: '0.85em', marginTop: 8 }}
                  label={undefined}
                />
                <Skeleton
                  style={{ width: '78%', height: '0.85em', marginTop: 8 }}
                  label={undefined}
                />
              </div>
              <div
                className="release-meta-panel release-meta-inline"
                aria-hidden
              >
                <dl className="release-meta-list">
                  <div className="release-meta-item">
                    <dt>Catalog</dt>
                    <dd>
                      <Skeleton
                        className="skeleton-meta-dd"
                        style={{ width: '4.5em' }}
                        label={undefined}
                      />
                    </dd>
                  </div>
                  <div className="release-meta-item">
                    <dt>Date</dt>
                    <dd>
                      <Skeleton
                        className="skeleton-meta-dd"
                        style={{ width: '7.5em' }}
                        label={undefined}
                      />
                    </dd>
                  </div>
                  <div className="release-meta-item">
                    <dt>Label</dt>
                    <dd>
                      <Skeleton
                        className="skeleton-meta-dd"
                        style={{ width: '6em' }}
                        label={undefined}
                      />
                    </dd>
                  </div>
                  <div className="release-meta-item release-meta-listen">
                    <dt>Listen</dt>
                    <dd>
                      <Skeleton
                        className="skeleton-meta-dd"
                        style={{ width: '9em' }}
                        label={undefined}
                      />
                    </dd>
                  </div>
                </dl>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/** Full photo detail chrome for direct `/photo/:id` loads before post data exists. */
export function PhotoDetailSkeleton() {
  return (
    <div
      className="detail-view photo-detail-view"
      aria-busy="true"
      aria-label="Loading photo"
    >
      <div className="detail-scroll">
        <div className="detail-align">
          <div className="detail-heading">
            <div className="detail-heading-lead">
              <div className="detail-heading-main">
                <Skeleton
                  className="skeleton-detail-title"
                  style={{ width: '52%', height: '1.15em' }}
                  label="Loading photo"
                />
              </div>
              <div
                className="detail-title-actions detail-title-actions--desktop"
                aria-hidden
              >
                <Skeleton className="skeleton-detail-action" label={undefined} />
                <Skeleton className="skeleton-detail-action" label={undefined} />
                <div className="detail-title-stepper" aria-hidden>
                  <Skeleton className="skeleton-detail-action" label={undefined} />
                  <Skeleton className="skeleton-detail-action" label={undefined} />
                </div>
              </div>
            </div>
            <p className="detail-artist" aria-hidden>
              <Skeleton
                className="skeleton-detail-artist"
                style={{ width: '22%', height: '0.85em' }}
                label={undefined}
              />
            </p>
            <div
              className="detail-heading-transport skeleton-detail-transport"
              aria-hidden
            >
              <Skeleton className="skeleton-detail-circle" label={undefined} />
              <Skeleton className="skeleton-detail-play" label={undefined} />
              <Skeleton className="skeleton-detail-circle" label={undefined} />
              <Skeleton className="skeleton-detail-circle" label={undefined} />
            </div>
          </div>
          <div className="release-about photo-description" aria-hidden>
            <Skeleton
              style={{ width: '100%', height: '0.85em' }}
              label={undefined}
            />
            <Skeleton
              style={{ width: '92%', height: '0.85em', marginTop: 8 }}
              label={undefined}
            />
            <Skeleton
              style={{ width: '76%', height: '0.85em', marginTop: 8 }}
              label={undefined}
            />
          </div>
          <div
            className="photo-location-under-caption photo-meta-location-link photo-meta-location-link--caption"
            aria-hidden
          >
            <Skeleton className="skeleton-location-pin" label={undefined} />
            <Skeleton
              className="skeleton-meta-dd"
              style={{ width: '8em' }}
              label={undefined}
            />
          </div>
        </div>
      </div>
      <div className="photo-meta-panel" aria-hidden>
        <dl className="photo-meta-list" aria-label="Photo details">
          <div className="photo-meta-item">
            <dt>Format</dt>
            <dd>
              <Skeleton
                className="skeleton-meta-dd"
                style={{ width: '4em' }}
                label={undefined}
              />
            </dd>
          </div>
          <div className="photo-meta-item">
            <dt>Item</dt>
            <dd>
              <Skeleton
                className="skeleton-meta-dd"
                style={{ width: '3.5em' }}
                label={undefined}
              />
            </dd>
          </div>
          <div className="photo-meta-item">
            <dt>Location</dt>
            <dd>
              <Skeleton
                className="skeleton-meta-dd"
                style={{ width: '9em' }}
                label={undefined}
              />
            </dd>
          </div>
          <div className="photo-meta-item">
            <dt>Views</dt>
            <dd>
              <Skeleton
                className="skeleton-meta-dd"
                style={{ width: '3em' }}
                label={undefined}
              />
            </dd>
          </div>
        </dl>
      </div>
    </div>
  );
}
